import { Alert, AlertDescription, AlertTitle } from "#components/ui/alert"
import { Button } from "#components/ui/button"
import { useI18n } from "#hooks/use-i18n"
import { slideUp } from "#lib/animations"
import { cn } from "#lib/utils"
import { AnimatePresence, motion } from "framer-motion"
import { AlertCircle, X } from "lucide-react"

interface ErrorAlertProps {
  error: string | null
  onDismiss?: () => void
  className?: string
  title?: string
}

const dismissButtonAnimation = {
  initial: { opacity: 0, scale: 0.8 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.8 },
  transition: { duration: 0.2 },
}

const DismissButton = ({ onDismiss }: { onDismiss: () => void }) => (
  <motion.div {...dismissButtonAnimation} className="absolute top-3 right-3">
    <Button
      variant="ghost"
      size="icon"
      className="h-6 w-6 rounded-full hover:bg-destructive/20"
      onClick={onDismiss}
    >
      <X className="h-4 w-4" />
    </Button>
  </motion.div>
)

export function ErrorAlert({ error, onDismiss, className, title }: ErrorAlertProps) {
  const { t } = useI18n()
  const errorTitle = title || t("common.error")

  // Map backend error codes to translation keys
  const getTranslatedError = (errorMessage: string): string => {
    // Try to match common error patterns
    if (errorMessage.includes("not found")) {
      return t("errors.common.notFound")
    }
    if (errorMessage.includes("unauthorized") || errorMessage.includes("authentication")) {
      return t("errors.auth.authenticationRequired")
    }
    if (errorMessage.includes("forbidden")) {
      return t("errors.common.forbidden")
    }
    if (errorMessage.includes("bad request")) {
      return t("errors.common.badRequest")
    }
    // Return original message if no match found
    return errorMessage
  }

  return (
    <AnimatePresence mode="wait">
      {error && (
        <motion.div
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={slideUp}
          className={cn("mb-4", className)}
        >
          <Alert variant="destructive" className="relative">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{errorTitle}</AlertTitle>
            <AlertDescription className="pr-8">{getTranslatedError(error)}</AlertDescription>
            {onDismiss && <DismissButton onDismiss={onDismiss} />}
          </Alert>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
